import { AlertClassColour, ConfigSystemParams, msSize } from 'src/app/core/consts';
import { catchError, Subscription, throwError } from 'rxjs';
import { Component, EventEmitter, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { ModalForceCloseService } from 'src/app/core/services/utelize/general/modal-force-close-service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PartnerClientLinkAddDTOe_EmptyObject } from 'src/app/core/models/system/partners/partner-client-link.dto';
import { PartnerClientLinksService } from 'src/app/core/services/utelize/system/partners/partner-client-links-service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-partner-link-modal-form',
  templateUrl: './partner-link-modal-form.component.html',
  styleUrls: ['./partner-link-modal-form.component.scss']
})

export class PartnerLinkModalFormComponent implements OnDestroy, OnInit {

  @Output() partnerLinkFormCancelled: EventEmitter<void>=new EventEmitter<void>();
  @Output() partnerLinkSaved        : EventEmitter<void>=new EventEmitter<void>();

  @ViewChild('partnerLinkModalFormContent', { static: false }) partnerLinkModalFormContent!: TemplateRef<void>;

  get modalBackGround() { let value: string | boolean=this.configSystemService.getValue(ConfigSystemParams.ModalBackGround, "TRUE");
                          return value=value.toUpperCase()=="STATIC" ? "static" : (value.toUpperCase()=="TRUE" ? true : false)     ; }

  private partnerLinkModalFormRef: NgbModalRef | undefined;
  private saveSub                : Subscription | undefined;

  public accuracyLevels     : Array<{ id: number, name: string }>=[ { id: 1, name: "Country"     },
                                                                    { id: 2, name: "Town / City" },
                                                                    { id: 3, name: "Cell Tower"  } ];
  public alertColourProgress: string=AlertClassColour.Grey;
  public clientCompanyId    : number=0;
  public formSubmitted      : boolean=false;
  public partnerCompanyId   : number=0;
  public partnerLinkForm!   : UntypedFormGroup;
  public refMaxLength       : number=100;
  public saving             : boolean=false;

  constructor(
    private configSystemService      : ConfigSystemService,
    private exceptionsService        : ExceptionsService,
    private formBuilder              : UntypedFormBuilder,
    private modalForceCloseService   : ModalForceCloseService,
    private modalService             : NgbModal,
    private partnerClientLinksService: PartnerClientLinksService
  ) { }

  public ngOnInit(): void {
    try {
      this.buildForm();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.saveSub?.unsubscribe();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Private

  private buildForm(): void {
    try {
      this.partnerLinkForm=this.formBuilder.group({
        agreedAccuracyLevel: [null],
        approvedUtc        : [""],
        includeDescendants : [false],
        partnerClientRef   : ["", [Validators.maxLength(this.refMaxLength)]],
        startUtc           : [""]
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private openModal(): void {
    try {
      this.partnerLinkModalFormRef=this.modalService.open(this.partnerLinkModalFormContent, {
        backdrop     : this.modalBackGround,
        beforeDismiss: () => { return this.modalForceCloseService.isForcing(); },
        centered     : true,
        keyboard     : false,
        size         : msSize.Medium
      });
    }
    catch(e) {
      this.partnerLinkFormCancelled.emit();
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private toUtcIso(value: string): string | undefined {
    try {
      if (value==undefined || value.trim()=="")
        return undefined;

      return new Date(value + "T00:00:00Z").toISOString();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }

  //#endregion

  //#region Public

  get form() {
    return this.partnerLinkForm.controls;
  }

  get companiesValid(): boolean {
    return this.partnerCompanyId>0 && this.clientCompanyId>0 && this.partnerCompanyId != this.clientCompanyId;
  }

  public cancelForm(): void {
    try {
      this.partnerLinkFormCancelled.emit();
      this.partnerLinkModalFormRef?.close();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public clientCompanyOnChange(companyId: number): void {
    try {
      this.clientCompanyId=Number(companyId ?? 0);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public openForm(): void {
    try {
      this.alertColourProgress=AlertClassColour.Grey;
      this.clientCompanyId    =0;
      this.formSubmitted      =false;
      this.partnerCompanyId   =0;
      this.saving             =false;
      this.buildForm();
      this.openModal();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public partnerCompanyOnChange(companyId: number): void {
    try {
      this.partnerCompanyId=Number(companyId ?? 0);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public submitForm(): void {
    try {
      if (this.saving)
        return;

      this.formSubmitted=true;

      if (!this.partnerLinkForm.valid || !this.companiesValid)
        return;

      const link=PartnerClientLinkAddDTOe_EmptyObject();
      const level=this.form['agreedAccuracyLevel'].value;
      const ref  =(<string>(this.form['partnerClientRef'].value ?? "")).trim();

      link.agreedAccuracyLevel=level==null ? undefined : Number(level);
      link.approvedUtc        =this.toUtcIso(this.form['approvedUtc'].value);
      link.includeDescendants =this.form['includeDescendants'].value==true;
      link.partnerClientRef   =ref=="" ? undefined : ref;
      link.startUtc           =this.toUtcIso(this.form['startUtc'].value);

      this.saving=true;

      this.saveSub?.unsubscribe();
      this.saveSub=this.partnerClientLinksService.add(this.partnerCompanyId, this.clientCompanyId, link)
                   .pipe(
                     catchError((error) => {
                       return throwError(() => new Error(this.exceptionsService.formatHttpError(error)));
                     })
                   )
                   .subscribe({
                     next : () => {
                       this.saving             =false;
                       this.alertColourProgress=AlertClassColour.Green;
                       this.partnerLinkSaved.emit();
                       this.partnerLinkModalFormRef?.close();
                     },
                     error: error => {
                       this.saving             =false;
                       this.alertColourProgress=AlertClassColour.Red;
                       this.exceptionsService.handleException(new Error(error));
                     }
                   });
    }
    catch(e) {
      this.saving=false;
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
